import { useState } from 'react';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { useMe } from '../session/SessionProvider';

type Item = { id: string; kind: string; title: string; author: string | null; description: string | null; url: string | null; tags: string[]; file: { mime: string; kind: string; sizeBytes: number; name: string } | null };
const KIND: Record<string, string> = { libro: 'Libro', estudio: 'Estudio', video: 'Video', audio: 'Audio', curso: 'Curso', predicacion: 'Predicación', otro: 'Otro' };

/** Biblioteca digital de la iglesia. */
export function LibraryPage() {
  const me = useMe();
  const base = `/media/churches/${me.activeChurchId}/library`;
  const data = useAsync((signal) => (me.activeChurchId ? api.get<{ canManage: boolean; items: Item[] }>(base, signal) : Promise.resolve(null)), [base]);
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('');
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState<{ tone: 'error' | 'success'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  if (!me.activeChurchId) return <EmptyState title="Elige primero una iglesia." />;
  const items = data.status === 'ready' && data.data ? data.data.items.filter((i) => (!kind || i.kind === kind) && (!q || `${i.title} ${i.author ?? ''} ${i.tags.join(' ')}`.toLowerCase().includes(q.toLowerCase()))) : [];

  async function add(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    const file = f.get('file');
    setBusy(true);
    setMsg(null);
    try {
      let mediaId: string | null = null;
      if (file instanceof File && file.size > 0) mediaId = (await api.upload<{ id: string }>(`/media/churches/${me.activeChurchId}/resources`, file)).id;
      await api.post(base, {
        kind: String(f.get('kind')), title: String(f.get('title') ?? '').trim(), author: String(f.get('author') ?? '').trim() || null, description: String(f.get('description') ?? '').trim() || null,
        mediaId, url: String(f.get('url') ?? '').trim() || null, tags: String(f.get('tags') ?? '').split(',').map((t) => t.trim()).filter(Boolean),
      });
      form.reset();
      setOpen(false);
      setMsg({ tone: 'success', text: 'Agregado a la biblioteca.' });
      data.reload();
    } catch (err) {
      setMsg({ tone: 'error', text: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="page">
      <header className="page__header">
        <h1>Biblioteca</h1>
        <p className="muted">Libros, estudios, videos, audios, cursos y predicaciones de la iglesia.</p>
      </header>
      {msg ? <Alert tone={msg.tone}>{msg.text}</Alert> : null}
      {data.status === 'ready' && data.data?.canManage && !open ? <div><Button onClick={() => setOpen(true)}>Agregar</Button></div> : null}
      {open ? (
        <Card title="Agregar a la biblioteca">
          <form className="form" onSubmit={(e) => void add(e)}>
            <div className="form__row">
              <div className="field"><label htmlFor="lb-kind">Tipo</label><select id="lb-kind" name="kind">{Object.entries(KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
              <Field label="Título" name="title" required minLength={2} maxLength={200} />
            </div>
            <Field label="Autor" name="author" maxLength={150} />
            <div className="field"><label htmlFor="lb-file">Archivo (PDF, audio, video…)</label><input id="lb-file" name="file" type="file" /></div>
            <Field label="…o enlace" name="url" maxLength={500} placeholder="https://…" />
            <Field label="Descripción" name="description" maxLength={2000} />
            <Field label="Etiquetas (separadas por coma)" name="tags" />
            <p className="small muted">Sube solo material con permiso para compartirse.</p>
            <div className="actions"><Button type="submit" busy={busy}>Agregar</Button><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button></div>
          </form>
        </Card>
      ) : null}
      <div className="form__row">
        <div className="field"><label htmlFor="lb-q">Buscar</label><input id="lb-q" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Título, autor o tema" /></div>
        <div className="field"><label htmlFor="lb-k">Tipo</label><select id="lb-k" value={kind} onChange={(e) => setKind(e.target.value)}><option value="">Todo</option>{Object.entries(KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
      </div>
      {data.status === 'loading' ? <Loading /> : null}
      {data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {data.status === 'ready' && items.length === 0 ? <Card><EmptyState title="Nada por aquí todavía." /></Card> : null}
      <div className="library-grid">
        {items.map((i) => (
          <Card key={i.id} title={i.title} action={<span className="small muted">{KIND[i.kind]}</span>}>
            {i.author ? <p className="small">{i.author}</p> : null}
            {i.description ? <p className="small pre-line">{i.description}</p> : null}
            {i.file?.kind === 'audio' ? <audio controls preload="none" src={`/api${base}/${i.id}/file`} /> : null}
            <div className="actions actions--wrap">
              {i.file ? <a className="btn btn--secondary" href={`/api${base}/${i.id}/file${i.file.mime === 'application/pdf' ? '?ver=1' : ''}`} target="_blank" rel="noreferrer">{i.file.mime === 'application/pdf' ? 'Leer' : 'Abrir'}</a> : null}
              {i.url ? <a className="btn btn--secondary" href={i.url} target="_blank" rel="noreferrer">Abrir enlace</a> : null}
              {data.status === 'ready' && data.data?.canManage ? <Button variant="ghost" onClick={() => { if (window.confirm('¿Quitar de la biblioteca?')) void api.delete(`${base}/${i.id}`).then(() => data.reload()); }}>Quitar</Button> : null}
            </div>
            {i.tags.length ? <p className="small muted">#{i.tags.join(' #')}</p> : null}
          </Card>
        ))}
      </div>
    </div>
  );
}
